import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ClinicalEventEntry, type EntryDescriptor } from './ClinicalEventEntry';
import { activeBirthEvents } from './birthCalculations';
import type { BirthWorkspaceProps } from './LaborFlowsheet';
const descriptors: EntryDescriptor[] = [
  {
    eventType: 'VITALS.BP',
    label: 'Maternal blood pressure',
    subjectTypes: ['MOTHER'],
    fields: [
      { key: 'systolic', label: 'Systolic (mmHg)', kind: 'number' },
      { key: 'diastolic', label: 'Diastolic (mmHg)', kind: 'number' },
    ],
  },
  {
    eventType: 'VITALS.PULSE',
    label: 'Maternal pulse',
    subjectTypes: ['MOTHER'],
    fields: [{ key: 'rate', label: 'Maternal pulse (/min)', kind: 'number' }],
  },
  {
    eventType: 'VITALS.TEMP',
    label: 'Maternal temperature',
    subjectTypes: ['MOTHER'],
    fields: [
      { key: 'value_c', label: 'Maternal temperature (°C)', kind: 'number' },
      { key: 'method', label: 'Maternal temperature method' },
    ],
  },
  { eventType: 'PP.FUNDUS', label: 'Maternal fundus', subjectTypes: ['MOTHER'] },
  { eventType: 'PP.BLOOD_LOSS', label: 'Additional maternal blood loss', subjectTypes: ['MOTHER'] },
  {
    eventType: 'VITALS.PULSE',
    label: 'Newborn heart rate',
    subjectTypes: ['NEWBORN'],
    fields: [{ key: 'rate', label: 'Newborn heart rate (/min)', kind: 'number' }],
  },
  {
    eventType: 'VITALS.TEMP',
    label: 'Newborn temperature',
    subjectTypes: ['NEWBORN'],
    fields: [
      { key: 'value_c', label: 'Newborn temperature (°C)', kind: 'number' },
      { key: 'method', label: 'Measurement method' },
    ],
  },
  { eventType: 'NB.FEEDING', label: 'Feeding / first latch', subjectTypes: ['NEWBORN'] },
  { eventType: 'NB.LOCATION', label: 'Newborn location', subjectTypes: ['NEWBORN'] },
];
export function CombinedDyadEntry(props: BirthWorkspaceProps) {
  const [open, setOpen] = useState(false);
  const births = activeBirthEvents(props.events, props.episode.id, new Date().toISOString()).filter(
    (e) =>
      (e as { encounter_id?: string }).encounter_id === props.encounterId &&
      e.event_type === 'BIRTH' &&
      e.payload.outcome === 'Live birth',
  );
  const local = (props.pendingEntries ?? []).filter(
    (e) =>
      (e as { encounter_id?: string }).encounter_id === props.encounterId &&
      e.event_type === 'BIRTH' &&
      e.payload.outcome === 'Live birth' &&
      ['pending', 'retryable-error', 'sign-in-required'].includes(e.pendingState),
  );
  const subjects = props.episode.subjects.filter(
    (s) =>
      s.active !== false &&
      (s.subject_type === 'MOTHER' ||
        (s.subject_type === 'NEWBORN' && [...births, ...local].some((e) => e.subject_id === s.fetus_subject_id))),
  );
  return (
    <section data-encounter-draft className="flex flex-col gap-3 rounded border p-4">
      <h3 className="font-semibold">Combined mother and newborn check</h3>
      <p className="text-sm text-muted-foreground">
        One observation time and one atomic submission. Complete only the observed sections; choose the newborn
        explicitly in multiple gestations.
      </p>
      <Button
        type="button"
        className="min-h-11 self-start"
        variant="outline"
        disabled={props.disabled || !subjects.some((s) => s.subject_type === 'NEWBORN')}
        onClick={() => setOpen(true)}>
        Open combined check / first latch
      </Button>
      {open && (
        <ClinicalEventEntry
          descriptors={descriptors}
          subjects={subjects}
          events={props.events}
          timeZone={props.timeZone}
          disabled={props.disabled}
          onSave={props.onSave}
        />
      )}
    </section>
  );
}
